export interface CongestionResult {
  center: [number, number];
  streetName: string;
  currentSpeed: number;
  freeFlowSpeed: number;
}

// bbox de Santa Coloma de Gramenet: lon_min,lat_min,lon_max,lat_max
const SANTA_COLOMA_BBOX = '2.190,41.438,2.228,41.468';

// Puntos de sondeo de flujo TomTom sobre zonas del municipio.
// PLACEHOLDER — nombres de zona provisionales (el GeoJSON OSM no trae
// nombres de via). Las coordenadas sí son reales: clusters de
// semaforos del municipio. Sustituir nombres al recibir datos de ARS93.
const FLOW_PROBE_POINTS: { name: string; lat: number; lon: number }[] = [
  { name: 'Zona centro', lat: 41.4496, lon: 2.2031 },
  { name: 'Zona sur', lat: 41.4446, lon: 2.2104 },
  { name: 'Eje norte', lat: 41.4571, lon: 2.2085 },
  { name: 'Eje oeste', lat: 41.4475, lon: 2.1980 },
  { name: 'Eje este', lat: 41.4528, lon: 2.2216 },
  { name: 'Zona Riu Besos', lat: 41.4503, lon: 2.2049 },
  { name: 'Acceso B-20', lat: 41.4516, lon: 2.2183 },
  { name: 'Zona estacion', lat: 41.4537, lon: 2.1985 },
  { name: 'Corredor central', lat: 41.4519, lon: 2.2061 },
  { name: 'Zona mercado', lat: 41.4544, lon: 2.2049 },
  { name: 'Acceso sur', lat: 41.4436, lon: 2.2058 },
  { name: 'Zona parque', lat: 41.4490, lon: 2.2036 },
];

async function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

interface FlowProbe {
  name: string;
  center: [number, number];
  currentSpeed: number;
  freeFlowSpeed: number;
  score: number; // lower = more interesting (slower traffic)
}

async function probeFlow(apiKey: string): Promise<FlowProbe[]> {
  const results = await Promise.allSettled(
    FLOW_PROBE_POINTS.map(async (pt) => {
      const url =
        `https://api.tomtom.com/traffic/services/4/flowSegmentData/relative0/15/json` +
        `?key=${apiKey}&point=${pt.lat},${pt.lon}`;
      const resp = await fetchWithTimeout(url, 3000);
      if (!resp.ok) return null;
      const data = await resp.json();
      const seg = data?.flowSegmentData;
      if (!seg) return null;
      // Score: combine absolute speed and ratio — lower = more congested
      // A street at 15 km/h (even if ratio=1.0) is more interesting than 50 km/h at ratio=0.9
      const ratio = seg.freeFlowSpeed > 0 ? seg.currentSpeed / seg.freeFlowSpeed : 1;
      const score = seg.currentSpeed * 0.6 + ratio * 40;
      return {
        name: pt.name,
        center: [pt.lon, pt.lat] as [number, number],
        currentSpeed: seg.currentSpeed,
        freeFlowSpeed: seg.freeFlowSpeed,
        score,
      };
    })
  );

  const probes: FlowProbe[] = [];
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value) {
      // Discard closed/pedestrian roads: freeFlow < 10 km/h or currentSpeed=0 with no real flow
      const v = r.value;
      if (v.freeFlowSpeed < 10) continue;
      if (v.currentSpeed === 0 && v.freeFlowSpeed > 0) continue;
      probes.push(v);
    }
  }
  return probes;
}

async function tryIncidentJams(apiKey: string): Promise<CongestionResult | null> {
  const fields = encodeURIComponent(
    '{incidents{type,geometry{type,coordinates},properties{iconCategory,magnitudeOfDelay,delay,from,to}}}'
  );
  // Only real traffic events: 1=accident, 6=jam
  const url =
    `https://api.tomtom.com/traffic/services/5/incidentDetails` +
    `?key=${apiKey}&bbox=${SANTA_COLOMA_BBOX}&fields=${fields}` +
    `&language=es-ES&categoryFilter=1,6`;

  const resp = await fetchWithTimeout(url, 3000);
  if (!resp.ok) return null;

  const data = await resp.json();
  const incidents = data?.incidents;
  if (!Array.isArray(incidents) || incidents.length === 0) return null;

  // Pick highest delay
  incidents.sort((a: any, b: any) => (b.properties?.delay ?? 0) - (a.properties?.delay ?? 0));
  const best = incidents[0];
  const coords = best.geometry?.coordinates;
  if (!coords || coords.length === 0) return null;

  const mid = coords[Math.floor(coords.length / 2)];
  const center: [number, number] = best.geometry.type === 'Point'
    ? [coords[0], coords[1]]
    : [mid[0], mid[1]];

  return {
    center,
    streetName: best.properties?.from || best.properties?.to || 'zona centro',
    currentSpeed: 0,
    freeFlowSpeed: 0,
  };
}

export async function detectCongestion(apiKey: string): Promise<CongestionResult | null> {
  // Run both APIs in parallel
  const [flowProbes, jamResult] = await Promise.all([
    probeFlow(apiKey).catch(() => [] as FlowProbe[]),
    tryIncidentJams(apiKey).catch(() => null),
  ]);

  // Pick the slowest flow probe — only if there's real congestion (ratio < 0.7)
  if (flowProbes.length > 0) {
    flowProbes.sort((a, b) => a.score - b.score);
    const best = flowProbes[0];
    const ratio = best.freeFlowSpeed > 0 ? best.currentSpeed / best.freeFlowSpeed : 1;
    if (ratio < 0.7) {
      return {
        center: best.center,
        streetName: best.name,
        currentSpeed: best.currentSpeed,
        freeFlowSpeed: best.freeFlowSpeed,
      };
    }
  }

  // Fallback: real jam incident
  if (jamResult) return jamResult;

  return null;
}
